import React from 'react';
import { Container, Box, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import generateBirthChartPDF from '../components/BirthChartPDF';

const BirthChartPage = () => {
  const planetaryPositions = [
    { planet: '⬆ Ascendant', sign: 'Aries', degree: '12° 45\'', house: '1st', color: '#FF6B6B' },
    { planet: '☉ Sun', sign: 'Aries', degree: '15° 23\'', house: '1st', color: '#FFD700' },
    { planet: '☽ Moon', sign: 'Taurus', degree: '8° 47\'', house: '2nd', color: '#C0C0C0' },
    { planet: '☿ Mercury', sign: 'Aries', degree: '22° 15\'', house: '1st', color: '#808080' },
    { planet: '♀ Venus', sign: 'Pisces', degree: '3° 56\'', house: '12th', color: '#FF69B4' },
    { planet: '♂ Mars', sign: 'Gemini', degree: '12° 38\'', house: '3rd', color: '#DC143C' },
    { planet: '♃ Jupiter', sign: 'Leo', degree: '27° 09\'', house: '5th', color: '#FF8C00' },
    { planet: '♄ Saturn', sign: 'Capricorn', degree: '19° 44\'', house: '10th', color: '#4169E1' },
    { planet: '♅ Uranus', sign: 'Aquarius', degree: '5° 21\'', house: '11th', color: '#00CED1' },
    { planet: '♆ Neptune', sign: 'Pisces', degree: '14° 33\'', house: '12th', color: '#9370DB' },
    { planet: '♇ Pluto', sign: 'Capricorn', degree: '23° 17\'', house: '10th', color: '#8B0000' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Your 2026 Transit Chart
      </Typography>

      {/* Planetary Positions Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Planetary Positions & Degrees
          </Typography>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => generateBirthChartPDF()}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            Download PDF Report
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Planet</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Sign</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Degree</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>House</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {planetaryPositions.map((position, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:hover': { bgcolor: 'grey.50' },
                    bgcolor: position.planet.includes('Ascendant') ? 'rgba(255, 107, 107, 0.1)' : 'transparent'
                  }}
                >
                  <TableCell sx={{ fontWeight: position.planet.includes('Ascendant') ? 'bold' : 'normal', fontSize: '1.1rem' }}>
                    {position.planet}
                  </TableCell>
                  <TableCell>{position.sign}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '1rem' }}>{position.degree}</TableCell>
                  <TableCell>{position.house}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Active Aspects Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          Active Aspects
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Planets</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Aspect</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Orb</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                <TableCell>Sun ⚹ Moon</TableCell>
                <TableCell>Trine</TableCell>
                <TableCell>3°12'</TableCell>
                <TableCell><Box component="span" sx={{ color: 'blue' }}>●</Box> Harmonious</TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                <TableCell>Venus ☐ Mars</TableCell>
                <TableCell>Square</TableCell>
                <TableCell>2°45'</TableCell>
                <TableCell><Box component="span" sx={{ color: 'red' }}>●</Box> Challenging</TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                <TableCell>Jupiter ⚹ Neptune</TableCell>
                <TableCell>Trine</TableCell>
                <TableCell>1°28'</TableCell>
                <TableCell><Box component="span" sx={{ color: 'blue' }}>●</Box> Harmonious</TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                <TableCell>Saturn ☍ Uranus</TableCell>
                <TableCell>Opposition</TableCell>
                <TableCell>4°03'</TableCell>
                <TableCell><Box component="span" sx={{ color: 'red' }}>●</Box> Challenging</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Generate Buttons Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
          Generate Reports
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600, mx: 'auto' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => generateBirthChartPDF()}
            sx={{
              py: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              }
            }}
          >
            Generate Natal Planetary Positions
          </Button>

          <Button
            variant="contained"
            size="large"
            startIcon={<DownloadIcon />}
            sx={{
              py: 2,
              bgcolor: '#4CAF50',
              '&:hover': {
                bgcolor: '#45a049',
              }
            }}
          >
            Generate Natal Chart Report
          </Button>

          <Button
            variant="contained"
            size="large"
            startIcon={<DownloadIcon />}
            sx={{
              py: 2,
              bgcolor: '#FF8C42',
              '&:hover': {
                bgcolor: '#e67a35',
              }
            }}
          >
            Generate Extended Natal Chart Report
          </Button>
        </Box>
      </Paper>

      {/* Birth Chart Visualization */}
      <Paper sx={{ p: 4, textAlign: 'center', minHeight: 500 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Birth Chart Wheel with 2026 Transits
        </Typography>

        <Box sx={{
          width: '100%',
          height: 400,
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 2,
          my: 3
        }}>
          <Typography color="text.secondary">
            [Birth chart visualization will appear here]
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          How to Read Your Chart
        </Typography>
        <Typography paragraph>
          • Inner wheel: Your natal chart positions<br />
          • Outer wheel: 2026 transit positions<br />
          • Red lines: Challenging aspects (squares, oppositions)<br />
          • Blue lines: Harmonious aspects (trines, sextiles)<br />
          • Green lines: Minor aspects
        </Typography>
      </Box>
    </Container>
  );
};

export default BirthChartPage;